import { useState } from 'react';
import { isValid } from 'date-fns';
import { DatePicker, type DatePickerProps } from '@mui/x-date-pickers';
import { DateFormat } from './DateFormat';
import { Input } from '@shared';

interface Props extends Omit<DatePickerProps<Date, Date>, 'renderInput'> {
  name?: string;
  required?: boolean;
  margin?: 'normal' | 'dense' | 'none';
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  validate?: boolean;
}

export const CustomDatePicker = (props: Props) => {
  const {
    name,
    label,
    margin,
    fullWidth,
    required,
    error,
    helperText,
    validate = false,
    inputFormat = DateFormat.SHORT,
    value,
    onChange,
    ...rest
  } = props;

  const [isError, setIsError] = useState<boolean>(false);
  const [shouldShowError, setShouldShowError] = useState<boolean>(false);

  const handleDateChange = (date: Date | null) => {
    setIsError(false);

    if (date === null) {
      onChange?.(date);
      return;
    }

    const isValidDate = date && isValid(date);
    if (validate && isValidDate) {
      onChange?.(date);
    } else if (validate && !isValidDate) {
      setIsError(true);
    } else {
      onChange?.(date);
    }
  };

  return (
    <DatePicker
      label={`${label}${required ? ' *' : ''}`}
      inputFormat={inputFormat}
      value={value || null}
      onChange={handleDateChange}
      renderInput={(params) => {
        const displayError = error || (validate && shouldShowError && isError);
        const displayHelperText = error ? helperText : validate && shouldShowError && isError ? 'Invalid date' : '';

        return (
          <Input
            {...params}
            name={name}
            margin={margin}
            fullWidth={fullWidth}
            placeholder={inputFormat.toLowerCase()}
            error={displayError}
            helperText={displayHelperText}
            onBlur={() => setShouldShowError(true)}
            onFocus={() => setShouldShowError(false)}
          />
        );
      }}
      {...rest}
    />
  );
};
